import { IconChevronRight } from '@repo/svgs';
import type { Building } from '@repo/api';
import { BreadcrumbLink } from '@/components/ui/BreadcrumbLink';
import { cn } from '@/lib/cn';
import { buildingsHubUrl } from '@/utils/routes';
import { neighborhoodHubUrl } from '@/lib/seo/buildingsHubs';

interface BuildingBreadcrumbsProps {
  building: Building;
  city?: string;
}

export function BuildingBreadcrumbs({ building, city }: BuildingBreadcrumbsProps) {
  const { primaryAddress, neighborhood, parentNeighborhood } = building;
  const borough = neighborhood?.borough || '';
  const parentName = parentNeighborhood?.name;
  const parentSlug = parentNeighborhood?.slug;
  const neighborhoodName = neighborhood?.name;
  const neighborhoodSlug = neighborhood?.slug || '';

  // Determine if this is NYC or Boston based on city param or neighborhood city
  const isNYC = city === 'nyc' || neighborhood?.city === 'New York';
  const isBoston = city === 'boston' || neighborhood?.city === 'Boston';

  // Configure city-specific breadcrumb settings. NYC only — the Boston arm
  // was removed with the `/cities/boston` tree it linked into (see the early
  // return below).
  //
  // Hub links go straight to `/buildings/{borough}/{neighborhood}`. The old
  // `/buildings/borough/{borough}` and `/buildings/{neighborhood}` forms
  // only reached it through redirects, and for The Bronx ("the-bronx") and
  // any neighborhood missing from the redirect map they ended on `/buildings`.
  // Neighborhoods with no hub page (e.g. "All Downtown") return null and are
  // shown as plain text.
  const getCityConfig = () => {
    return {
      rootHref: '/buildings',
      rootLabel: 'NYC buildings',
      neighborhoodHref: (slug: string) => neighborhoodHubUrl(borough, slug),
      parentHref: (slug: string) => neighborhoodHubUrl(borough, slug),
      showBorough: true,
    };
  };

  /*
   * Boston shows no breadcrumbs.
   *
   * Every link in the Boston trail pointed into the legacy `/cities/boston`
   * tree, which has been deleted — Boston has no `/buildings` coverage to
   * point at instead, so there is no valid trail to render. Bailing here beats
   * rendering unlinked labels: a breadcrumb whose ancestors go nowhere is
   * worse than no breadcrumb.
   *
   * The NYC trail is untouched; it points into `/buildings`, which is live.
   *
   * Note the BreadcrumbList JSON-LD needs no matching change:
   * BuildingStructuredData only mounts when `city === 'nyc'`, so its own
   * `isBoston` branch has never executed.
   */
  if (isBoston) return null;

  const config = getCityConfig();
  const parentHref = parentSlug ? config.parentHref(parentSlug) : null;
  const neighborhoodHref = neighborhoodSlug ? config.neighborhoodHref(neighborhoodSlug) : null;

  return (
    <div
      className={cn(
        'flex gap-1 items-center py-0',
        // Mobile: one line, scrolled horizontally. `[&>*]:shrink-0` keeps the
        // crumbs at full width so they overflow instead of compressing.
        'flex-nowrap overflow-x-auto overscroll-x-contain no-scrollbar [&>*]:shrink-0',
        // `-mx-6` cancels the header's `px-6` so the strip spans the full
        // viewport and overflow is clipped at the screen edge rather than
        // inset from it — a crumb cut off at the edge is the cue that there
        // is more. `px-6` puts the first and last crumb back in line with
        // the page gutter at either end of the scroll.
        '-mx-6 px-6',
        // Desktop has room for the trail, so let it wrap as before.
        'md:flex-wrap md:overflow-x-visible md:mx-0 md:px-0 md:w-full',
      )}
    >
      <BreadcrumbLink href={config.rootHref} className="text-caption text-accent py-1">
        {config.rootLabel}
      </BreadcrumbLink>

      <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />

      {config.showBorough && borough && (
        <>
          <BreadcrumbLink href={buildingsHubUrl(borough)} className="text-caption text-accent py-1">
            {borough}
          </BreadcrumbLink>
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
        </>
      )}

      {parentName && parentSlug && (
        <>
          {parentHref ? (
            <BreadcrumbLink href={parentHref} className="text-caption text-accent py-1">
              {parentName}
            </BreadcrumbLink>
          ) : (
            <BreadcrumbLink active className="text-caption text-secondary py-1">
              {parentName}
            </BreadcrumbLink>
          )}
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
        </>
      )}

      {neighborhoodName && neighborhoodSlug && (
        <>
          {neighborhoodHref ? (
            <BreadcrumbLink href={neighborhoodHref} className="text-caption text-accent py-1">
              {neighborhoodName}
            </BreadcrumbLink>
          ) : (
            <BreadcrumbLink active className="text-caption text-secondary py-1">
              {neighborhoodName}
            </BreadcrumbLink>
          )}
          <IconChevronRight className="w-2 h-2 text-secondary shrink-0" />
        </>
      )}

      <BreadcrumbLink active className="text-caption text-secondary py-1">
        {primaryAddress?.streetAddress}
      </BreadcrumbLink>
    </div>
  );
}
